import React, { useEffect, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import { Link } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useAppSelector } from "../../store";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Button from "../../components/common/Button";
import dashboardService, { DashboardStats } from "../../features/dashboard/dashboardService";
import { toast } from "react-toastify";
import PersonalChatbotSelectionPage from "../personal/PersonalChatbotSelectionPage";

// ChartJS bileşenlerini kaydet
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DashboardPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await dashboardService.getDashboardStats();
        setStats(data);
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
        // Don't show error toast, just log it. We will show default 0 values.
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Helper to check roles
  const hasRole = (role: string) => user?.roles?.includes(role);
  const isSuperAdmin = hasRole("superadmin") || hasRole("admin");
  const isAgencyOwner = hasRole("agency_owner");
  const isCompanyOwner = hasRole("company_owner");
  const isAgencySpecialist = hasRole("marketing_specialist");
  const isCompanySpecialist = hasRole("company_specialist");
  const isPersonal = hasRole("personal");

  if (isPersonal) {
    return <PersonalChatbotSelectionPage />;
  }

  // Default empty stats if none returned
  const displayStats = stats || {
    cards: {},
    recent_companies: [],
    client_activity: [],
    recent_conversations: []
  };

  // Chart Data Generators (Dynamic based on stats)
  const getConversationChartData = () => {
    // Mock data for now as backend doesn't return time-series yet
    return {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      datasets: [
        {
          label: "Conversations",
          data: [0, 0, 0, 0, 0, 0], // Default 0
          fill: true,
          backgroundColor: "rgba(37, 99, 235, 0.1)",
          borderColor: "#2563EB",
          tension: 0.4,
        },
      ],
    };
  };

  const getClientActivityChartData = () => {
    const activity = displayStats.client_activity || [];

    // If no activity, show empty chart structure
    const labels = activity.length > 0 ? activity.map(c => c.name) : ["No Data"];
    const data = activity.length > 0 ? activity.map(c => c.count) : [0];

    return {
      labels: labels,
      datasets: [
        {
          label: "Conversations",
          data: data,
          backgroundColor: "rgba(139, 92, 246, 0.8)",
          borderRadius: 8,
        },
      ],
    };
  };

  if (loading) {
    return (
      <LoadingSpinner
        size={40}
        text="Loading Dashboard"
        subtitle="Preparing your AI insights..."
        fullHeight={true}
      />
    );
  }

  return (
    <div className="dashboard-page">
      {/* Brand Header */}
      <div className="brand-header slide-in-down">
        <div className="brand-content-wrapper">
          <div className="brand-title-area">
            <div className="brand-icon-wrapper">
              <img
                src="/icon.svg"
                alt="Meylo Icon"
                width="48"
                height="48"
                className="animated-brand-icon-small"
              />
            </div>
            <div className="brand-text">
              <h1>Meylo Dashboard</h1>
              <p>Welcome back, {user?.full_name || "User"}!</p>
            </div>
          </div>
          {/** 
          <div className="brand-actions">
            <Link to="/reports/generate">
              <Button
                variant="primary"
                className="btn-premium-glow"
                leftIcon={<i className="bi bi-stars"></i>}
              >
                Generate Report
              </Button>
            </Link>
          </div>
          */}
        </div>
      </div>

      {/* Stats Grid - Dynamic Content */}
      <div className="stats-grid">
        {/* Superadmin Stats */}
        {isSuperAdmin && (
          <>
            <StatCard
              label="Total Companies"
              value={displayStats.cards?.total_companies}
              icon="bi-building"
              link="/companies"
            />
            <StatCard
              label="Total Users"
              value={displayStats.cards?.total_users}
              icon="bi-people"
              link="/users"
            />
            <StatCard
              label="Total Chatbots"
              value={displayStats.cards?.total_chatbots}
              icon="bi-robot"
              link="/ai-chatbots"
            />
            <StatCard
              label="Total Tokens"
              value={displayStats.cards?.total_tokens?.toLocaleString()}
              icon="bi-cpu"
              link="/usage"
            />
          </>
        )}

        {/* Agency Owner Stats */}
        {(isAgencyOwner || isAgencySpecialist) && (
          <>
            <StatCard
              label="Total Clients"
              value={displayStats.cards?.total_clients}
              icon="bi-briefcase"
              link="/client-companies"
            />
            <StatCard
              label="Managed Chatbots"
              value={displayStats.cards?.total_managed_chatbots}
              icon="bi-robot"
              link="/ai-chatbots"
            />
            <StatCard
              label="Agency Users"
              value={displayStats.cards?.total_agency_users}
              icon="bi-people"
              link="/my-agency/my-team"
            />
            {/* Token usage hidden for Agency Owner as requested */}
          </>
        )}

        {/* Company Owner Stats */}
        {(isCompanyOwner || isCompanySpecialist) && (
          <>
            <StatCard
              label="Total Conversations"
              value={displayStats.cards?.total_conversations}
              icon="bi-chat-dots"
              link="/ai-chatbots"
            />
            <StatCard
              label="Active Chatbots"
              value={displayStats.cards?.active_chatbots}
              icon="bi-robot"
              trend="Active"
              trendType="positive"
              link="/ai-chatbots"
            />
            <StatCard
              label="Total Chatbots"
              value={displayStats.cards?.total_chatbots}
              icon="bi-collection"
              trend="Total"
              trendType="neutral"
              link="/ai-chatbots"
            />
            <StatCard
              label="Company Users"
              value={displayStats.cards?.total_company_users}
              icon="bi-people"
              link="/my-company/my-team"
            />
            {/* Token usage hidden for Company Owner as requested */}
          </>
        )}
      </div>

      {/* Charts & Lists Grid */}
      <div className="charts-grid">
        {/* Client Activity Chart (Agency Only) */}
        {(isAgencyOwner || isAgencySpecialist) && (
          <div className="chart-card slide-in-up animate-delay-2">
            <div className="card-header">
              <h5 className="card-title">
                <i className="bi bi-bar-chart-fill me-2"></i>
                Client Activity
              </h5>
            </div>
            <div className="card-body">
              <div className="chart-container">
                <Bar
                  data={getClientActivityChartData()!}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Recent Companies List (Superadmin Only) */}
        {isSuperAdmin && (
          <div className="chart-card slide-in-up animate-delay-2">
            <div className="card-header">
              <h5 className="card-title">
                <i className="bi bi-clock-history me-2"></i>
                Recent Registrations
              </h5>
            </div>
            <div className="card-body">
              <div className="activities-list">
                {displayStats.recent_companies && displayStats.recent_companies.length > 0 ? (
                  displayStats.recent_companies.map((company) => (
                    <div key={company.id} className="activity-item">
                      <div className="activity-icon primary">
                        <i className="bi bi-building"></i>
                      </div>
                      <div className="activity-content">
                        <h6 className="activity-title">{company.name}</h6>
                        <p className="activity-description">
                          {company.type} company registered
                        </p>
                        <small className="activity-time">
                          {new Date(company.created_at).toLocaleDateString()}
                        </small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-4">No recent registrations</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recent Conversations List (Company Only) */}
        {(isCompanyOwner || isCompanySpecialist) && (
          <div className="chart-card slide-in-up animate-delay-2">
            <div className="card-header">
              <h5 className="card-title">
                <i className="bi bi-chat-left-text me-2"></i>
                Recent Conversations
              </h5>
            </div>
            <div className="card-body">
              <div className="activities-list">
                {displayStats.recent_conversations && displayStats.recent_conversations.length > 0 ? (
                  displayStats.recent_conversations.map((conv) => (
                    <div key={conv.id} className="activity-item">
                      <div className="activity-icon success">
                        <i className="bi bi-chat-dots"></i>
                      </div>
                      <div className="activity-content">
                        <h6 className="activity-title">{conv.bot_name}</h6>
                        <p className="activity-description">
                          Session: {conv.session_id.substring(0, 8)}...
                        </p>
                        <small className="activity-time">
                          {new Date(conv.created_at).toLocaleString()}
                        </small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-4">No recent conversations</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Reusable Stat Card Component
const StatCard: React.FC<{
  label: string;
  value: string | number | undefined;
  icon: string;
  trend?: string;
  trendType?: "positive" | "negative" | "neutral";
  link?: string;
}> = ({ label, value, icon, trend, trendType = "neutral", link }) => {
  const CardContent = (
    <div className={`stat-card premium-card slide-in-up animate-delay-1 ${link ? 'cursor-pointer hover-lift' : ''}`}>
      <div className="stat-content-minimal">
        <div className="stat-header">
          <h6 className="stat-label">{label}</h6>
          <div className="stat-icon-minimal">
            <i className={`bi ${icon}`}></i>
          </div>
        </div>
        <div className="stat-body">
          <div className="stat-value">{value !== undefined ? value : 0}</div>
          {trend && (
            <div className={`stat-change ${trendType}`}>
              {trendType === "positive" && <i className="bi bi-arrow-up-short"></i>}
              {trendType === "negative" && <i className="bi bi-arrow-down-short"></i>}
              <span>{trend}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (link) {
    return (
      <Link to={link} style={{ textDecoration: 'none', color: 'inherit' }}>
        {CardContent}
      </Link>
    );
  }

  return CardContent;
};

export default DashboardPage;
